import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { extractUrlParameters } from 'shared/services/windowLocationService';


const
	showFilters = ref(false),
	videoUrl = ref(null),
	videoIsPlaying = ref(false),
	cardLabel = ref(''),
	slideIndexForReminderToPlace = ref(0),
	scrollbarWidth = ref(null),
	displaySearchOnHero = ref(false),
	isListLayout = ref(false),
	isEventCalendar = ref(false),
	clearAllFiltersLabel = ref(''),
	filtersLabel = ref(''),
	monthFiltersLabel = ref(''),
	resetFiltersLabel = ref(''),
	searchBarPlaceholder = ref(''),
	applyFiltersLabel = ref('');

export default function useContentLibrary(
	vm,
	store,
	isOnHero
) {
	store = store || useStore();
	const { commit, dispatch, getters } = store;

	if (vm) {
		cardLabel.value = vm.BaseViewModel.GoToItemLabel;
		isListLayout.value = vm.BaseViewModel.LayoutType === 1;
		isEventCalendar.value = vm.BaseViewModel.LayoutType === 2;
		clearAllFiltersLabel.value = vm.BaseViewModel.ClearAllFiltersLabel;
		filtersLabel.value = vm.BaseViewModel.FiltersLabel;
		monthFiltersLabel.value = vm.BaseViewModel.MonthFiltersLabel;
		resetFiltersLabel.value = vm.BaseViewModel.ResetFiltersLabel;
		searchBarPlaceholder.value = vm.BaseViewModel.SearchBarPlaceholder;
		applyFiltersLabel.value = vm.BaseViewModel.ApplyFiltersLabel ?? '';
	}
	displaySearchOnHero.value = displaySearchOnHero.value || isOnHero;

	const
		language = computed(() => getters.getCulture),
		contentFacets = computed(() => getters.getContentSearchFacets),
		contentFacetGroups = computed(() => getters.getContentFacetGroups),
		sortedContentFacets = computed(
			() => getters.getSortedContentSearchFacets
		),
		eventCalendarTabs = computed(() => getters.getEventCalendarTabs),
		paginatedResults = computed(() => getters.getContentPaginatedResults),
		tab = computed(() => getters.getContentTab),
		initialTab = computed(() => getters.getContentInitialTab),
		pageNumber = computed(() => getters.getContentPageNumber),
		searchItemsPerPage = computed(() => getters.getContentItemsPerPage),
		expectedResultsNumberOnPage = computed(
			() => getters.getContentExpectedResultsNumberOnPage
		),
		searchPhrase = computed(() => getters.getContentSearchPhrase),
		queryString = computed(() => getters.getContentQueryString),
		constantSearchParams = computed(() => getters.getConstantSearchParams),
		isFirstPageLoaded = computed(() => getters.getIsFirstPageLoaded),
		activeSidebarFiltersType = computed(
			() => getters.getActiveSidebarFiltersType
		),
		totalResultsCount = computed(() => getters.getTotalResultsCount),
		contentLibraryComposable = computed(
			() => getters.getContentLibraryComposable
		),
		pagination = computed(() => getters.getLibraryPagination);

	const
		currentTab = computed(() => tab.value || initialTab.value),
		isCurrentTabPastEvents = computed(
			() => currentTab.value.term === vm.BaseViewModel.PastEventsLabel
		),
		filtersAreVisible = computed(
			() => contentFacetGroups.value?.length > 0
		),
		contentFacetFilters = computed(() =>
			contentFacetGroups.value.filter((element) =>
				element.data.some(
					(subElement) => subElement.subGroupName == null
				)
			)
		),
		contentFacetMonthFilters = computed(() =>
			contentFacetGroups.value.filter((element) =>
				element.data.some(
					(subElement) => subElement.subGroupName !== null
				)
			)
		),
		contentFiltersAreVisible = computed(
			() => contentFacetFilters.value?.length > 0
		),
		monthFiltersAreVisible = computed(
			() => contentFacetMonthFilters.value?.length > 0
		),
		bothFiltersTypesApplied = computed(
			() => monthFiltersAreVisible.value && contentFiltersAreVisible.value
		),
		resultsCount = computed(() =>
			currentTab.value?.count
				? currentTab.value.count
				: totalResultsCount.value
		),
		formattedApplyFilterLabel = computed(() => {
			return applyFiltersLabel.value
				.replace('{n}', Math.max(resultsCount.value, 0) || '')
				.replace(/\s+/g, ' ');
		}),
		libraryTabs = computed(() =>
			!isEventCalendar.value
				? sortedContentFacets.value
				: eventCalendarTabs.value
		),
		groupedPaginatedResultsLength = computed(() => {
			if (isEventCalendar.value) {
				return paginatedResults.value
					.map((result) => {
						return result.items;
					})
					.flat().length;
			}
		});

	function calculateScrollbarWidth() {
		scrollbarWidth.value = window.innerWidth - document.body.clientWidth;
	}
	function formatDisplayName(tabItem) {
		if (tabItem.name) {
			return tabItem.name;
		} else {
			return `${tabItem.displayName} (${tabItem.count})`
				.replace(/([A-Z])/g, ' $1')
				.trim();
		}
	}
	function setSidebarFilters(filtersType) {
		commit('setActiveSidebarFiltersType', filtersType);
		filtersToggled();
	}
	function filtersToggled() {
		showFilters.value = !showFilters.value;
		calculateScrollbarWidth();

		if (showFilters.value) {
			document.body.style.overflowY = 'hidden';
			if (scrollbarWidth.value && scrollbarWidth.value > 0) {
				document.body.style.paddingRight = `${scrollbarWidth.value}px`;
			}
		} else {
			document.body.removeAttribute('style');
		}
	}
	async function search() {
		await commit('setContentSearchPhrase', searchPhrase.value);
		dispatch('executeSearchWithFilters');
	}
	function fetchContentCards(tabItem, isInfiniteScroll) {
		let facet;

		if (
			isEventCalendar.value &&
			tabItem.name === vm.BaseViewModel.PastEventsLabel
		) {
			commit('setIsPastEventDisplayed', true);
		} else {
			commit('setIsPastEventDisplayed', false);
		}

		if (
			tabItem &&
			initialTab.value &&
			tabItem.term !== initialTab.value.term
		) {
			facet = tabItem.term;
		}
		if (!isInfiniteScroll) {
			!isEventCalendar.value
				? dispatch('fetchContentResults', { fc: facet })
				: dispatch('fetchContentResults', {
						fc: '',
						clearParameters: true,
				  });
		} else {
			dispatch('fetchContentResults', { searchParams: true });
		}
	}
	function changeTabAndResetPage(tabItem) {
		if (tabItem.term === currentTab.value.term) {
			return;
		} else {
			commit(
				'setContentExpectedResultsNumberOnPage',
				searchItemsPerPage.value
			);
			commit('resetContentPageNumber');
			fetchContentCards(tabItem);
		}
	}
	function showMoreItems() {
		commit('setContentPageNumber');
		if (
			(currentTab.value &&
				!isEventCalendar.value &&
				resultsCount.value > paginatedResults.value.length) ||
			(currentTab.value &&
				isEventCalendar.value &&
				resultsCount.value > groupedPaginatedResultsLength.value)
		) {
			commit(
				'setContentExpectedResultsNumberOnPage',
				expectedResultsNumberOnPage.value + searchItemsPerPage.value
			);
			fetchContentCards(currentTab.value, true);
		}
	}
	function setVideoUrl(value) {
		videoUrl.value = value;
		videoIsPlaying.value = true;
	}
	function closePopup() {
		videoUrl.value = null;
		videoIsPlaying.value = false;
	}
	function setIndexWhereReminderPlaced() {
		const screen = window.innerWidth;
		if (screen < 481) {
			slideIndexForReminderToPlace.value = 3;
		} else {
			slideIndexForReminderToPlace.value = screen < 800 ? 6 : 9;
		}
	}
	function initContentLibrary() {
		if (isEventCalendar.value) {
			commit('setIsEventCalendarLibrary');
			commit('setEventCalendarTabs', [
				{
					term: vm.BaseViewModel.UpcomingEventsLabel,
					name: vm.BaseViewModel.UpcomingEventsLabel,
				},
				{
					term: vm.BaseViewModel.PastEventsLabel,
					name: vm.BaseViewModel.PastEventsLabel,
				},
			]);
		}
		commit('setConstantSearchParams', {
			FacetConfigId: vm.BaseViewModel.ContentLink,
			ps: vm.BaseViewModel.SearchItemsPageSize,
			ct: vm.BaseViewModel.PageTypeFilter,
			ca: vm.BaseViewModel.ContentAreaCategory[0],
			pn: 1,
			language: language.value,
		});
		commit('setContentItemsPerPage', +vm.BaseViewModel.SearchItemsPageSize);
		commit(
			'setContentExpectedResultsNumberOnPage',
			+vm.BaseViewModel.SearchItemsPageSize
		);
		let searchParamsObj = {},
			isLoadedWithParams = false;
		if (window.location.search !== '') {
			isLoadedWithParams = true;
			searchParamsObj = extractUrlParameters(window.location.search);
			commit('setContentQueryString', searchParamsObj);
			if (searchParamsObj.q && searchParamsObj.q !== '') {
				commit('setContentSearchPhrase', searchParamsObj.q);
			}
		}
		dispatch('fetchContentResults', {
			fc: searchParamsObj.fc,
			searchParams: isLoadedWithParams,
		});
		if (!isEventCalendar.value && !isListLayout.value) {
			setIndexWhereReminderPlaced();
			window.addEventListener('resize', setIndexWhereReminderPlaced);
		}
		else {
			slideIndexForReminderToPlace.value = 0;
		}
	}

	if (vm) {
		initContentLibrary();
	}

	function paginationClickHandler(pageNumber) {
		dispatch('paginationLibraryClickHandler', pageNumber);
		fetchContentCards(currentTab.value, !!isEventCalendar.value);
    }

	const scrollBlockIntoView = () => {
		document.getElementById('content-library').scrollIntoView({ behavior: 'instant' });
	};

	return {
		// data
		showFilters,
		videoUrl,
		videoIsPlaying,
		cardLabel,
		slideIndexForReminderToPlace,
		scrollbarWidth,
		displaySearchOnHero,
		// computed
		language,
		contentFacets,
		contentFacetGroups,
		sortedContentFacets,
		eventCalendarTabs,
		paginatedResults,
		tab,
		initialTab,
		pageNumber,
		searchItemsPerPage,
		expectedResultsNumberOnPage,
		searchPhrase,
		queryString,
		constantSearchParams,
		isFirstPageLoaded,
		activeSidebarFiltersType,
		totalResultsCount,
		contentLibraryComposable,
		currentTab,
		isCurrentTabPastEvents,
		filtersAreVisible,
		contentFacetFilters,
		contentFacetMonthFilters,
		contentFiltersAreVisible,
		monthFiltersAreVisible,
		bothFiltersTypesApplied,
		isListLayout,
		isEventCalendar,
		resultsCount,
		formattedApplyFilterLabel,
		libraryTabs,
		groupedPaginatedResultsLength,
		clearAllFiltersLabel,
		filtersLabel,
		monthFiltersLabel,
		resetFiltersLabel,
		searchBarPlaceholder,
		// methods
		calculateScrollbarWidth,
		formatDisplayName,
		setSidebarFilters,
		filtersToggled,
		search,
		fetchContentCards,
		changeTabAndResetPage,
		showMoreItems,
		setVideoUrl,
		closePopup,
		setIndexWhereReminderPlaced,

		pagination,
		paginationClickHandler,
		scrollBlockIntoView,
	};
}
